import { IPhotoSize } from "types/types";
import { ApiService } from "./apiService";

export interface LeafCategoryOption {
  id: string;
  pathEN: string;
  pathAR: string;
  // How full the leaf is: products filed there already, and raw products still
  // waiting to be. Zero products with rows waiting is a category to work on.
  // Only the leaf list carries them - a card's own candidates are what the
  // category pass searched, and say nothing about the rest of the queue.
  products?: number;
  waiting?: number;
}

// The review queue, read off the raw products themselves. Every row that has not
// become a product is in it: there is no verdict table deciding who gets looked
// at, so a row is here because nobody has confirmed it yet.
export type RawProductStage = 'needs_category' | 'needs_info' | 'ready';

// One thing the buyer takes out of the pack: what it is, how many of them the
// pack states, and why it is in there. An ingredient is not one of these - it is
// what the product is made of, not a thing anybody picks up.
export interface RawProductContentEntry {
  contentName: string;
  // Null where the pack prints no number for it. A count nobody printed is not
  // a count, so the card shows the thing without one rather than inventing "1".
  amount: number | null;
  amountUnit: string;
  analysis: string;
}

export interface RawProductContentsLanguage {
  introduction: string;
  contents: RawProductContentEntry[] | null;
}

export interface RawProductContents {
  ar: RawProductContentsLanguage;
  en: RawProductContentsLanguage;
}

// One thing the buyer takes out of the pack as a step refers to it: the short
// handle rather than the paragraph the contents pass writes. The id is the
// label's own marker where it prints one - a kit numbering its parts 1, 2A, 2B
// has already said which bottle each step means.
export interface RawProductUsageComponent {
  id: string;
  name: string;
  amount: string;
  role: string;
}

// One procedure, start to finish. A second usage exists only where it can be
// performed without performing the first - a body wash that is also a shampoo -
// so the parts of a kit are steps here, never usages of their own.
export interface RawProductUsage {
  // Both languages, like the instructions: an Arabic reader given an English
  // heading over Arabic steps is reading a procedure nobody wrote for them.
  // The Arabic is empty on rows written before the pass wrote one.
  name: {
    en: string;
    ar: string;
  };
  usageIntent: string;
  usageSubject: string;
  targetGender: string;
  routineContext: string;
  area: string;
  targetAreas: string[];
  frequency: string;
  reapplication: string;
  components: RawProductUsageComponent[];
  // The machine-readable steps. The card shows the written instructions instead:
  // they are the same sequence in the same order, in the words a person reads.
  steps: unknown[];
  instructions: {
    en: string[];
    ar: string[];
  };
}

// One ingredient as the panel declares it. `name` is the INCI, `asPrinted` is
// the one string the label actually carries - they differ often enough to matter,
// since "Green illite clay" becomes "Illite" and a customer cannot look that up.
// The brackets a panel puts after a name do two different jobs: `aliases` names
// the same substance again, `components` names what it is made of.
export interface RawProductIngredient {
  name: string;
  asPrinted: string;
  aliases: string[];
  components: string[];
}

// One declared ingredient under both names. The English is the panel's own INCI,
// which is what the row is matched and searched by; the Arabic is what an Arabic
// reader is shown, and empty where the pass could not name it in Arabic rather
// than filled with the INCI again.
export interface RawProductIngredientName {
  en: string;
  ar: string;
}

// The cautions this pack prints, in both languages and in the same order, read
// side by side. Only what the label says: the pass copies and never invents, so
// every line here can be held against a photograph.
export interface RawProductWarnings {
  en: string[];
  ar: string[];
}

export interface RawProductReviewItem {
  rawProductId: string;
  // The product this row became, if it became one. Null for everything in the
  // queue - a promoted row leaves it - so it is only ever set on the page that
  // asks for a single raw product by id.
  productId: string | null;
  barcode: string | null;
  suggestionId: string | null;
  images: IPhotoSize[];
  // Which of those photos shows the front of the pack. The strip marks it in
  // place rather than being reordered around it, so the photo a reviewer clicks
  // stays where they clicked it.
  avatarMediaId: string | null;
  ocrText: string;
  descriptionEn: string | null;
  descriptionAr: string | null;
  tagEn: string | null;
  tagAr: string | null;
  kind: string | null;
  kindTerm: string | null;
  targetAreas: string[];
  targetArea: string | null;
  targetAudience: string | null;
  // Who the pack addresses, from the same reading as the audience. Null is a
  // pack that addresses anybody, which the Arabic writes as the masculine.
  targetGender: string | null;
  form: string | null;
  packShape: string | null;
  nameEn: string | null;
  nameAr: string | null;
  // The brands row this pack was filed under. Null with a date is a pack that
  // names no brand; null without one is a pack nobody has asked.
  brand: RawProductBrand | null;
  brandCheckedAt: string | null;
  weight: number | null;
  drainedWeight: number | null;
  estimatedWeight: number | null;
  weightUnit: string | null;
  countryOfManufactureISO: string | null;
  countryOfRegistrationISO: string | null;
  price: number | null;
  // Where the product is filed: the graph's pick, or a reviewer's correction of
  // it. Promotion reads this.
  leafCategory: { id: string; name: string } | null;
  candidates: LeafCategoryOption[];
  // What the pack holds, and when the pass last looked. Null with a date is the
  // usual and a real answer - a cream in a tube holds nothing separate - while
  // null without one only means nobody has asked yet, and the card says which.
  contents: RawProductContents | null;
  contentsCheckedAt: string | null;
  // How the pack is used, and when the pass last looked. Null with a date is a
  // row the pass could not write for; null without one is one nobody has asked.
  usages: RawProductUsage[] | null;
  usagesCheckedAt: string | null;
  // What the panel declares. Null with a date is a pack whose panel was never
  // resolved; null without one is one nobody has asked yet.
  ingredients: RawProductIngredient[] | null;
  ingredientsCheckedAt: string | null;
  // The same panel in order of what explains the product, named in both
  // languages. An order rather than a key list and a rest: where a panel stops
  // mattering is a slope, so the screen decides how much of the top counts as
  // "key" rather than the pass cutting it somewhere.
  rankedIngredients: RawProductIngredientName[] | null;
  rankedIngredientsCheckedAt: string | null;
  // Null with a date is a pack that prints no cautions, which is most of them;
  // null without one is a pack nobody has asked yet.
  warnings: RawProductWarnings | null;
  warningsCheckedAt: string | null;
  // What the pack asserts about itself. Null with a date is a pack that asserts
  // nothing; null without one is a pack nobody has asked.
  attributes: RawProductAttributes | null;
  attributesCheckedAt: string | null;
  stage: RawProductStage;
  // What this row is still waiting for, in plain sentences.
  blockers: string[];
}

export interface RawProductReviewPage {
  page: number;
  limit: number;
  total: number;
  items: RawProductReviewItem[];
}

export interface ListRawProductsForReviewDto {
  page: number;
  limit: number;
  stage?: RawProductStage;
  // Any part of the number printed on the pack. Sent only when it has something
  // in it, so an empty box is the whole queue rather than a search for nothing.
  barcode?: string;
  // One row, by its id - what the details page asks for. Unlike the queue it
  // also answers for a raw product that has already been promoted.
  rawProductId?: string;
  // One leaf of the taxonomy, for working a category instead of the queue.
  leafCategoryId?: string;
}

export const getRawProductsForReview = async (
  dto: ListRawProductsForReviewDto,
): Promise<RawProductReviewPage> => {
  const response = await ApiService.get(`analyzer/raw-products/review`, { params: dto });
  return response.data;
};

// Turns a finished raw product into a real one. It refuses a row that is still
// missing something and says what - the same list the card shows as blockers.
export interface PromoteRawProductResult {
  rawProductId: string;
  productId: string;
  barcode: string | null;
}

export const promoteRawProduct = async (rawProductId: string): Promise<PromoteRawProductResult> => {
  const response = await ApiService.post(`analyzer/raw-products/${rawProductId}/promote`, {});
  return response.data?.data ?? response.data;
};

export interface RawProductByCategoryItem {
  id: string;
  barcode: string;
  suggestionId: string | null;
  // The product this row was promoted into, null while it is still only a raw
  // product.
  productId: string | null;
  images: IPhotoSize[];
  tagEn: string | null;
  tagAr: string | null;
  targetArea: string | null;
  targetAudience: string | null;
  // Who the pack addresses, from the same reading as the audience. Null is a
  // pack that addresses anybody, which the Arabic writes as the masculine.
  targetGender: string | null;
  form: string | null;
}

export interface RawProductsByCategoryPage {
  page: number;
  limit: number;
  total: number;
  items: RawProductByCategoryItem[];
}

export interface ListRawProductsByCategoryDto {
  categoryId: string;
  page: number;
  limit: number;
}

export const getRawProductsByCategory = async ({
  categoryId,
  page,
  limit,
}: ListRawProductsByCategoryDto): Promise<RawProductsByCategoryPage> => {
  const response = await ApiService.get(`analyzer/raw-products/by-category/${categoryId}`, {
    params: { page, limit },
  });
  return response.data;
};

// How many raw products sit under each category, subtree included, and how many
// of them became products. A category holding nothing is absent.
export type RawProductCategoryCounts = Record<string, { total: number; promoted: number }>;

export const getRawProductCategoryCounts = async (): Promise<RawProductCategoryCounts> => {
  const response = await ApiService.get(`analyzer/raw-products/category-counts`);
  return response.data;
};

// Moves a raw product to another leaf from the browse tree, and clears the form
// that put it in the wrong one.
export const moveRawProductToLeafCategory = async ({
  rawProductId,
  leafCategoryId,
}: {
  rawProductId: string;
  leafCategoryId: string;
}) => {
  const response = await ApiService.patch(`analyzer/raw-products/${rawProductId}/leaf-category`, {
    leafCategoryId,
  });
  return response.data;
};

export const getLeafCategories = async (): Promise<LeafCategoryOption[]> => {
  const response = await ApiService.get(`analyzer/raw-products/leaf-categories`);
  return response.data;
};

export const resetRawProductType = async (rawProductId: string) => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/reset-product-type`,
    {},
  );
  return response.data;
};

// Reads the pack's photos again. For a rejection whose reasoning is arguing
// about words that are plainly a bad scan rather than a bad decision.
export const reextractRawProductOcr = async (rawProductId: string) => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/reextract-ocr`,
    {},
  );
  return response.data;
};

// Reads the product's physical form again. Updates the review row too, so the
// card shows the new value rather than the one the verdict was reached against.
export const reextractRawProductForm = async (rawProductId: string) => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/reextract-form`,
    {},
  );
  return response.data;
};

// What the row is waiting for after a write. Carried by every answer that can
// change it, because the card's stage chip, promote button and missing-info list
// are otherwise read off the list payload - which was computed before the write.
// Recomputed server-side rather than worked out here: the rules live with
// promotion, and a second copy of them in the browser would be a second opinion.
export interface RawProductReadiness {
  stage: RawProductStage;
  blockers: string[];
}

export interface RedescribeRawProductResult extends RawProductReadiness {
  rawProductId: string;
  descriptionEn: string | null;
  descriptionAr: string | null;
}

// Writes the product's description again and answers with what the row now
// holds, so the card shows the stored sentences rather than hopeful ones.
export const redescribeRawProduct = async (
  rawProductId: string,
): Promise<RedescribeRawProductResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/redescribe`,
    {},
  );
  return response.data?.data ?? response.data;
};

// One or both sentences. A field left out is untouched, and null is the pack
// having no description worth writing - which is a real answer, and one
// promotion waits on.
export interface RawProductDescriptionPatch {
  descriptionEn?: string | null;
  descriptionAr?: string | null;
}

// A reviewer's rewrite of what the description pass wrote. Answers in the same
// shape as a re-read, so the card shows a typed sentence the way it shows a
// written one.
export const updateRawProductDescription = async ({
  rawProductId,
  description,
}: {
  rawProductId: string;
  description: RawProductDescriptionPatch;
}): Promise<RedescribeRawProductResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/description`,
    description,
  );
  return response.data?.data ?? response.data;
};

export interface ReextractRawProductContentsResult {
  rawProductId: string;
  contents: RawProductContents | null;
  contentsCheckedAt: string | null;
}

// Reads what is physically in the pack again. The batch skips a row it has
// already stamped, so this is the only way to ask a second time - and the answer
// carries the stamp, since a run that found nothing has to be able to clear a
// list the card is still showing.
//
// `mediaIds` are the photos to read, as ticked on the card. Empty is the whole
// strip: the choice belongs to this reading rather than to the product, so it is
// sent with the request and nothing stores it.
export const reextractRawProductContents = async ({
  rawProductId,
  mediaIds,
}: {
  rawProductId: string;
  mediaIds: string[];
}): Promise<ReextractRawProductContentsResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/reextract-contents`,
    { mediaIds },
  );
  return response.data?.data ?? response.data;
};

export interface ReextractRawProductUsagesResult {
  rawProductId: string;
  usages: RawProductUsage[] | null;
  usagesCheckedAt: string | null;
}

// Writes how the product is used again, from the photos ticked on the card. It
// reads the contents column on its way through, so a kit whose parts were read
// wrong is a contents refresh first and this one second. Empty `mediaIds` is the
// whole strip.
export const reextractRawProductUsages = async ({
  rawProductId,
  mediaIds,
}: {
  rawProductId: string;
  mediaIds: string[];
}): Promise<ReextractRawProductUsagesResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/reextract-usages`,
    { mediaIds },
  );
  return response.data?.data ?? response.data;
};

// What the pack holds, as a reviewer writes it. Both languages every time: they
// name the same things in the same order, and sending one alone is how the two
// lists stop lining up. An empty list is a pack that holds nothing separate.
export interface RawProductContentsPatch {
  en: RawProductContentsLanguage;
  ar: RawProductContentsLanguage;
}

export const updateRawProductContents = async ({
  rawProductId,
  contents,
}: {
  rawProductId: string;
  contents: RawProductContentsPatch;
}): Promise<ReextractRawProductContentsResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/contents`,
    contents,
  );
  return response.data?.data ?? response.data;
};

// One procedure a reviewer kept, and what they want it to say. sourceIndex is
// where it sits in the stored list: it is what carries the procedure's plan -
// its intent, its areas, its machine-readable steps - across a save that only
// touches the words a person reads.
export interface RawProductUsageEdit {
  sourceIndex: number;
  nameEn?: string | null;
  nameAr?: string | null;
  instructionsEn?: string[];
  instructionsAr?: string[];
}

// The whole list every time, in the order it should be in: an edit can remove a
// procedure or change how many there are, and one at a time cannot say that. An
// index left out is a procedure the reviewer dropped.
export const updateRawProductUsages = async ({
  rawProductId,
  usages,
}: {
  rawProductId: string;
  usages: RawProductUsageEdit[];
}): Promise<ReextractRawProductUsagesResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/usages`,
    { usages },
  );
  return response.data?.data ?? response.data;
};

export interface ReextractRawProductIngredientsResult {
  rawProductId: string;
  ingredients: RawProductIngredient[] | null;
  ingredientsCheckedAt: string | null;
}

export interface PreviewRawProductIngredientsResult {
  rawProductId: string;
  ingredients: RawProductIngredient[];
}

// Transcribes the ingredients panel from the photos ticked on the card, without
// storing it, for the reviewer to correct and confirm first. The pass cuts the
// panel out of the photographs it is given, so the ticks matter more here than
// anywhere else. Empty `mediaIds` is the whole strip.
export const previewRawProductIngredients = async ({
  rawProductId,
  mediaIds,
}: {
  rawProductId: string;
  mediaIds: string[];
}): Promise<PreviewRawProductIngredientsResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/preview-ingredients`,
    { mediaIds },
  );
  return response.data?.data ?? response.data;
};

// A reviewer's confirmed list. An empty list is a pack with no readable panel.
export const updateRawProductIngredients = async ({
  rawProductId,
  ingredients,
}: {
  rawProductId: string;
  ingredients: RawProductIngredient[];
}): Promise<ReextractRawProductIngredientsResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/ingredients`,
    { ingredients },
  );
  return response.data?.data ?? response.data;
};

// What the pack asserts about itself, as the attributes pass read it. Null is a
// pack that asserts none of it, which is the ordinary answer.
//
// Who the product is sold to and what stage of life it is for are missing on
// purpose: the audience pass settles those, and they are shown beside it.
// One value in both languages: the English is what the knowledge graph matches
// on, the Arabic is what an Arabic shopper reads on the product page.
export interface RawProductAttributeName {
  en: string;
  ar: string;
}

export interface RawProductAttributes {
  suitability: {
    hairTypes: RawProductAttributeName[];
    skinTypes: RawProductAttributeName[];
    scalpTypes: RawProductAttributeName[];
    beardTypes: RawProductAttributeName[];
    usageTimings: RawProductAttributeName[];
  };
  claims: {
    concerns: RawProductAttributeName[];
    goals: RawProductAttributeName[];
  };
  formulaClaims: RawProductAttributeName[];
  // Every colour the product comes in, where the colour is what is being bought.
  // Empty on everything else, one entry for an ordinary colour product, several
  // for a palette. The family and the undertone are what a filter runs on; the
  // shade and the code are what the pack prints.
  colors: RawProductShade[];
  // What the product smells of, where the pack says. Null where it says nothing,
  // and null where it states it has no fragrance - that is a formula claim and
  // sits with the formula claims.
  scent: RawProductScent | null;
  // What it tastes of, where taste is what is chosen: toothpaste, mouthwash, a
  // lip product. Null for everything else, however strongly it smells.
  flavor: RawProductFlavor | null;
}

export interface RawProductShade {
  family: RawProductAttributeName | null;
  shade: RawProductAttributeName | null;
  // As printed, and in one language because it is not language: a shade code is
  // an identifier a shopper matches character for character.
  code: string | null;
  undertone: RawProductAttributeName | null;
}

// A scent: the family a filter runs on, and the pack's own name for it.
export interface RawProductFlavor {
  family: RawProductAttributeName | null;
  name: RawProductAttributeName | null;
}

export interface RawProductScent {
  family: RawProductAttributeName | null;
  name: RawProductAttributeName | null;
}

export interface ReextractRawProductAttributesResult {
  rawProductId: string;
  attributes: RawProductAttributes | null;
  attributesCheckedAt: string | null;
}

// Reads what the pack asserts about itself again off its stored label text. No
// photos: every value has to be quoted from that text to survive.
// A reviewer can send a run to the description instead of the label. The pass
// does that by itself for a pack that claims nothing at all; this is for the
// pack that claims plenty and claims nothing worth keeping, which only somebody
// looking at the names can tell.
export const reextractRawProductAttributes = async (
  rawProductId: string,
  withDescription = false,
): Promise<ReextractRawProductAttributesResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/reextract-attributes${withDescription ? '?withDescription=true' : ''}`,
    {},
  );
  return response.data?.data ?? response.data;
};

// The six passes the attributes are read by, as the reload beside each heading
// on the edit modal asks for them. A pass answers everything it answers at once,
// so one reload can redraw several lists: the four type lists are one question,
// and so are the colour, the scent and the flavour.
export type AttributePart =
  | 'types'
  | 'concerns'
  | 'goals'
  | 'timings'
  | 'appearance'
  | 'formula-claims';

// One pass read again, on its own. What comes back is the whole column with that
// pass's fields rewritten, so the modal redraws those rows and leaves the rest of
// the reviewer's screen as it was.
export const reextractRawProductAttributePart = async ({
  rawProductId,
  part,
  withDescription = false,
}: {
  rawProductId: string;
  part: AttributePart;
  withDescription?: boolean;
}): Promise<ReextractRawProductAttributesResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/reextract-attributes/${part}${withDescription ? '?withDescription=true' : ''}`,
    {},
  );
  return response.data?.data ?? response.data;
};

// A reviewer's correction. The whole shape every time - these are short lists
// with nothing behind them, so there is nothing a field-at-a-time patch buys.
export const updateRawProductAttributes = async ({
  rawProductId,
  attributes,
}: {
  rawProductId: string;
  attributes: RawProductAttributes;
}): Promise<ReextractRawProductAttributesResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/attributes`,
    attributes,
  );
  return response.data?.data ?? response.data;
};

export interface ReextractRawProductWarningsResult {
  rawProductId: string;
  warnings: RawProductWarnings | null;
  warningsCheckedAt: string | null;
}

// The cautions this product should now carry, in the order they are read. Both
// languages every time: they are the same cautions written for their own
// readers, and sending one alone is how the two lists stop lining up.
export const updateRawProductWarnings = async ({
  rawProductId,
  warnings,
}: {
  rawProductId: string;
  warnings: RawProductWarnings;
}): Promise<ReextractRawProductWarningsResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/warnings`,
    warnings,
  );
  return response.data?.data ?? response.data;
};

// Reads the pack's printed cautions again off its stored label text. No photos
// to pick: this pass does not look at one.
export const reextractRawProductWarnings = async (
  rawProductId: string,
): Promise<ReextractRawProductWarningsResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/reextract-warnings`,
    {},
  );
  return response.data?.data ?? response.data;
};

export interface RankRawProductIngredientsResult {
  rawProductId: string;
  rankedIngredients: RawProductIngredientName[] | null;
  rankedIngredientsCheckedAt: string | null;
}

// Puts the stored panel in order of what explains the product and names every
// row in Arabic. It reads the ingredients column rather than the label, so a
// pack whose panel was never transcribed is refused - transcribe first, then
// rank. No photos to pick: nothing in this pass looks at one.
export const rankRawProductIngredients = async (
  rawProductId: string,
): Promise<RankRawProductIngredientsResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/rank-ingredients`,
    {},
  );
  return response.data?.data ?? response.data;
};

export interface RawProductIdentity {
  // The sentence the leaf search was actually run against. It sits with the
  // identity fields rather than the particulars because it is what the pack is,
  // not what is printed on it - and because correcting it is the same act as
  // correcting a kind or a form: it changes what a later pass reads.
  tagEn: string | null;
  tagAr: string | null;
  kind: string | null;
  targetAreas: string[];
  targetAudience: string | null;
  // Answered by the audience pass in the same call as the audience. Editable
  // because it is what every Arabic line on the product is addressed from, and
  // null - a pack that speaks to anybody - is a real answer, written as the
  // masculine.
  targetGender: string | null;
  form: string | null;
  packShape: string | null;
}

// One field at a time is the normal case - a reviewer corrects the reading they
// can see is wrong - so the patch carries only what changed. A field left out is
// untouched; a field sent as null is the pack saying nothing, which is an answer
// in its own right.
export type RawProductIdentityPatch = Partial<RawProductIdentity>;

export interface UpdateRawProductIdentityResult
  extends RawProductIdentity,
    RawProductReadiness {
  rawProductId: string;
  kindTerm: string | null;
  targetArea: string | null;
}

// A reviewer's correction to what the identity graphs read off the pack. It is
// written to the raw product, which is what every later pass reads; the review
// row is realigned with it server-side, so the answer carries both.
export const updateRawProductIdentity = async ({
  rawProductId,
  identity,
}: {
  rawProductId: string;
  identity: RawProductIdentityPatch;
}): Promise<UpdateRawProductIdentityResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/identity`,
    identity,
  );
  return response.data?.data ?? response.data;
};

export interface RawProductInfo {
  nameEn: string | null;
  nameAr: string | null;
  weight: number | null;
  drainedWeight: number | null;
  estimatedWeight: number | null;
  weightUnit: string | null;
  countryOfManufactureISO: string | null;
  countryOfRegistrationISO: string | null;
  price: number | null;
}

// One field at a time, like the identity patch: a field left out is untouched,
// a field sent as null is the pack stating none of it.
export type RawProductInfoPatch = Partial<RawProductInfo>;

export interface UpdateRawProductInfoResult extends RawProductInfo, RawProductReadiness {
  rawProductId: string;
}

export const updateRawProductInfo = async ({
  rawProductId,
  info,
}: {
  rawProductId: string;
  info: RawProductInfoPatch;
}): Promise<UpdateRawProductInfoResult> => {
  const response = await ApiService.patch(`analyzer/raw-products/${rawProductId}/info`, info);
  return response.data?.data ?? response.data;
};

// Reads the pack's particulars again for one product. The batch will not come
// back to a row it has already read, so this is the only way to correct a bad
// reading short of typing it out. Answers with what the row now holds.
export const reextractRawProductInfo = async (
  rawProductId: string,
): Promise<UpdateRawProductInfoResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/reextract-info`,
    {},
  );
  return response.data?.data ?? response.data;
};

export interface RawProductBrand {
  id: string;
  nameEn: string | null;
  nameAr: string | null;
  // Every way packs have printed this brand, which is what a reviewer checks a
  // merge against.
  aliases: string[];
}

// A brand as the picker lists it, with how many packs are filed under it.
export interface BrandOption extends RawProductBrand {
  productCount: number;
}

export const getBrands = async (): Promise<BrandOption[]> => {
  const response = await ApiService.get(`analyzer/raw-products/brands`);
  return response.data?.data ?? response.data;
};

// The brand chosen, or null; names only when they were changed. Names with no
// brand chosen make a new brand, and neither says the pack names no brand.
export interface RawProductBrandPatch {
  brandId: string | null;
  nameEn?: string | null;
  nameAr?: string | null;
}

export const updateRawProductBrand = async ({
  rawProductId,
  patch,
}: {
  rawProductId: string;
  patch: RawProductBrandPatch;
}): Promise<ReextractRawProductBrandResult> => {
  const response = await ApiService.patch(`analyzer/raw-products/${rawProductId}/brand`, patch);
  return response.data?.data ?? response.data;
};

export interface ReextractRawProductBrandResult {
  rawProductId: string;
  brand: RawProductBrand | null;
  brandCheckedAt: string | null;
}

// Reads one product's brand again and files it again. The batch will not come
// back to a row it has already read, so this is the only way to correct a bad
// reading. Answers with the brand the row now points at.
export const reextractRawProductBrand = async (
  rawProductId: string,
): Promise<ReextractRawProductBrandResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/reextract-brand`,
    {},
  );
  return response.data?.data ?? response.data;
};

export interface RawProductPipelineStep {
  step: 'kind' | 'area' | 'audience' | 'form' | 'packShape' | 'category';
  success: boolean;
  error?: string;
}

export interface RerunRawProductPipelineResult
  extends RawProductIdentity,
    RawProductReadiness {
  rawProductId: string;
  kindTerm: string | null;
  targetArea: string | null;
  leafCategory: { id: string; name: string } | null;
  // What each pass managed. A failing pass does not stop the ones after it, so a
  // run can half-succeed and this is what says which half.
  steps: RawProductPipelineStep[];
}

export type RawProductStep = RawProductPipelineStep['step'];

// One pass run again on its own, for the field a reviewer can see is wrong. The
// answer carries the whole identity block rather than the single field asked
// for: a pass writes more than the one value the card shows it under, so a
// narrower answer would leave half of what it wrote stale on screen.
export interface RerunRawProductStepResult extends RawProductIdentity, RawProductReadiness {
  rawProductId: string;
  step: RawProductStep;
  kindTerm: string | null;
  targetArea: string | null;
  leafCategory: { id: string; name: string } | null;
}

export const rerunRawProductStep = async ({
  rawProductId,
  step,
}: {
  rawProductId: string;
  step: RawProductStep;
}): Promise<RerunRawProductStepResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/rerun-step`,
    { step },
  );
  return response.data?.data ?? response.data;
};

export interface RunMissingRawProductPassesResult extends RawProductReadiness {
  rawProductId: string;
  steps: { step: string; success: boolean; error?: string }[];
  // Passes still waiting on something the row does not have yet (label text,
  // a front photo, ingredients to rank).
  skipped: string[];
}

// Runs only the passes this row has not been through, in dependency order.
// Contents is left out when `withContents` is false.
export const runMissingRawProductPasses = async ({
  rawProductId,
  withContents,
}: {
  rawProductId: string;
  withContents: boolean;
}): Promise<RunMissingRawProductPassesResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/run-missing`,
    { withContents },
  );
  return response.data?.data ?? response.data;
};

// Runs every pass that says what the product is, in the order they depend on
// each other, and answers with what the row now holds.
export const rerunRawProductPipeline = async (
  rawProductId: string,
): Promise<RerunRawProductPipelineResult> => {
  const response = await ApiService.patch(
    `analyzer/raw-products/${rawProductId}/rerun-pipeline`,
    {},
  );
  return response.data?.data ?? response.data;
};

export interface SetRawProductAvatarResult extends RawProductReadiness {
  rawProductId: string;
  avatarMediaId: string;
}

// A reviewer naming the front photo. The avatar pass chooses one already; this
// is how the ones it gets wrong are fixed, and the row is stamped so the batch
// does not undo the correction on its next run.
export const setRawProductAvatar = async ({
  rawProductId,
  mediaId,
}: {
  rawProductId: string;
  mediaId: string;
}): Promise<SetRawProductAvatarResult> => {
  const response = await ApiService.patch(`analyzer/raw-products/${rawProductId}/avatar`, {
    mediaId,
  });
  return response.data?.data ?? response.data;
};

export const resolveRawProductCategory = async ({
  rawProductId,
  leafCategoryId,
}: {
  rawProductId: string;
  leafCategoryId: string;
}) => {
  const response = await ApiService.patch(`analyzer/raw-products/${rawProductId}/category`, {
    leafCategoryId,
  });
  return response.data;
};
